import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Target,
    CheckCircle2,
    Circle,
    ShieldCheck,
    LayoutDashboard,
    ArrowRight,
    X,
    Check,
    Clock,
    Sparkles,
    Search,
    Bookmark,
    Briefcase,
    ChevronLeft,
    ChevronRight,
    Radio,
    Trash2
} from 'lucide-react';
import { toast } from 'react-toastify';

import { fetchPerplexitySignals } from '../../../redux/HSignalSlice/hsignalSlice';
import { fetchAllContactsByCompanyId } from '../../../redux/ContactSlice/contactSlice';
import { fetchSavedJobs, selectJobStrategy, clearJobStrategy } from '../../../redux/SavedJobSlice/savedJobSlice';
import { fetchSavedCompanies } from '../../../redux/SavedCompanySlice/savedCompanySlice';
import { fetchCompanies } from '../../../redux/CompanySlice/companySlice';
import {
    fetchTaskTimeline,
    makeDoneKey
    // fetchLinkedinDoneContacts, fetchFirstEmailDoneContacts, fetchDMDoneContacts,
    // fetchFollowupEmailDoneContacts — REMOVED: fetchTaskTimeline returns all task data;
    // the slice builds taskHistoryByContact from it, making these 4 calls redundant.
} from '../../../redux/DailyTasksSlice/dailyTaskSlice';
import { fetchActiveSprints, removeActiveSprint } from '../../../redux/ActiveSprintSlice/activeSprintSlice';

import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import DTCard from '../../../components/DailyTasksPage/DTContent/DTCard';
import { recommendStrategy } from '../../../utils/strategyRecommendation';
import { STRATEGY_CATALOG, STRATEGY_NAME_BY_ID, STRATEGY_IDS } from '../../../constants/strategyCatalog';
import {
    setCompanyRecommendation,
    dismissCompanyRecommendation,
    setCompanyStrategy,
    pushStrategyChangeEvent
} from '../../../redux/CompanyStrategySlice/companyStrategySlice';

// ── Stable empty-object sentinels ──
// Defined outside the component so they are the SAME reference on every render.
// useSelector with inline `|| {}` returns a NEW object every time, causing
// all downstream useMemo hooks to recompute even when nothing changed.
const EMPTY_OBJ = {};
const EMPTY_CONTACTS = {};

const SPRINT_DAYS = 14;

/**
 * A job "activated" from the outreach strategy modal carries its exact
 * day-by-day plan in strategyContextSnapshot. When present, Daily Tasks renders
 * that plan verbatim as a short sprint (its own length, e.g. 4 days) instead of
 * the generic 14-day generated cadence. Returns the plan array or null.
 */
const getOutreachPlan = (job) => {
    const snap = job?.strategyContextSnapshot;
    if (snap && snap.source === 'outreach-modal' && Array.isArray(snap.plan) && snap.plan.length) {
        return snap.plan;
    }
    return null;
};

/** The exact "who to contact" people chosen when the strategy was activated. */
const getOutreachContacts = (job) => {
    const snap = job?.strategyContextSnapshot;
    if (snap && snap.source === 'outreach-modal' && Array.isArray(snap.contacts)) {
        return snap.contacts;
    }
    return [];
};

/** Human label for the activated path, shown as a badge on the task card. */
const OUTREACH_PATH_TITLE = {
    referral: 'Get an Internal Referral',
    direct: 'Contact the Hiring Team Directly',
    signal: 'Warm a Hiring Signal'
};
const getOutreachPathTitle = (job) => OUTREACH_PATH_TITLE[job?.strategyContextSnapshot?.pathId] || null;

/** Shimmer block used to compose the loading skeleton. */
const Sk = ({ className = '' }) => <div className={`bg-slate-200/70 rounded ${className}`} />;

/** Loading skeleton mirroring the Daily Tasks board (rail · board · side). */
const DailyTasksSkeleton = () => (
    <div className="px-6 md:px-8 pb-10 grid grid-cols-1 xl:grid-cols-[272px_1fr_300px] gap-4 items-start animate-pulse">
        {/* LEFT — sprint rail */}
        <aside className="bg-white border border-[#ECECEC] rounded-2xl p-4 flex flex-col gap-3">
            <Sk className="h-4 w-28" />
            <Sk className="h-9 w-full rounded-lg" />
            <div className="flex gap-1.5">
                <Sk className="h-6 w-12 rounded-full" />
                <Sk className="h-6 w-14 rounded-full" />
                <Sk className="h-6 w-16 rounded-full" />
            </div>
            {[0, 1].map((i) => (
                <div key={i} className="rounded-xl border border-[#EEF0F4] p-3 flex flex-col gap-2">
                    <div className="flex items-center gap-2.5">
                        <Sk className="h-8 w-8 rounded-lg" />
                        <div className="flex-1 flex flex-col gap-1.5">
                            <Sk className="h-3 w-24" />
                            <Sk className="h-2.5 w-16" />
                        </div>
                    </div>
                    <Sk className="h-12 w-full rounded-lg" />
                </div>
            ))}
        </aside>

        {/* CENTER — board */}
        <div className="flex flex-col gap-4 min-w-0">
            <div className="rounded-2xl border border-indigo-100 p-5 flex items-center gap-3.5">
                <Sk className="h-11 w-11 rounded-xl" />
                <div className="flex-1 flex flex-col gap-2">
                    <Sk className="h-3 w-40" />
                    <Sk className="h-4 w-56" />
                </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl px-4 py-3.5 border border-[#ECECEC] flex flex-col gap-2.5">
                        <Sk className="h-2.5 w-16" />
                        <Sk className="h-6 w-10" />
                        <Sk className="h-2.5 w-20" />
                    </div>
                ))}
            </div>
            <div className="bg-white border border-[#ECECEC] rounded-2xl p-4 flex flex-col gap-3">
                <Sk className="h-3 w-24" />
                <div className="flex gap-2">
                    {[0, 1, 2, 3].map((i) => <Sk key={i} className="h-10 w-[76px] rounded-lg" />)}
                </div>
            </div>
            {[0, 1].map((i) => (
                <div key={i} className="bg-white border border-[#ECECEC] rounded-xl p-5 flex flex-col gap-3">
                    <div className="flex gap-2">
                        <Sk className="h-5 w-40 rounded-full" />
                        <Sk className="h-5 w-20 rounded-full" />
                    </div>
                    <Sk className="h-4 w-3/4" />
                    <Sk className="h-px w-full" />
                    <Sk className="h-12 w-full rounded-lg" />
                    <Sk className="h-12 w-full rounded-lg" />
                </div>
            ))}
        </div>

        {/* RIGHT — side panels */}
        <div className="flex flex-col gap-4">
            {[0, 1].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-[#ECECEC] flex flex-col gap-3">
                    <Sk className="h-3 w-32" />
                    <Sk className="h-3 w-full" />
                    <Sk className="h-3 w-5/6" />
                    <Sk className="h-16 w-full rounded-lg" />
                </div>
            ))}
        </div>
    </div>
);

/** Company logo tile with initials fallback (used by the v3 campaign rail + header). */
const LogoTile = ({ name, logo, size = 38 }) => {
    const initials = String(name || '?')
        .trim()
        .split(/\s+/)
        .map((s) => s[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    return (
        <div
            className="rounded-lg overflow-hidden shrink-0 flex items-center justify-center bg-slate-100 border border-slate-200 text-slate-500 font-bold select-none"
            style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
        >
            {logo ? (
                <img src={logo} alt={name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
                initials
            )}
        </div>
    );
};

const inferMissionIntent = (currentDay, taskName = '', channel = '') => {
    // An explicit plan-step channel beats any text/day-number heuristic —
    // copy edits ("drop a note" vs "send a message") must not change behavior.
    const normalizedChannel = String(channel || '').toLowerCase();
    if (normalizedChannel) {
        if (/email|gmail/.test(normalizedChannel)) return 'email';
        if (/\bdm\b|message|inmail/.test(normalizedChannel)) return 'message';
        if (/connect|invite|note/.test(normalizedChannel)) return 'connect';
    }

    const normalizedTask = String(taskName || '').toLowerCase();

    if (currentDay === 4 || currentDay === 6 || normalizedTask.includes('email')) return 'email';
    if (normalizedTask.includes('note')) return 'note';
    if (currentDay === 5 || normalizedTask.includes('message') || normalizedTask.includes('dm') || normalizedTask.includes('inmail')) return 'message';
    if (normalizedTask.includes('connect') || normalizedTask.includes('invite') || normalizedTask.includes('connection')) return 'connect';

    // Day 1: typical LinkedIn connection-first slot; day 2+ defaults to follow-up/message unless copy implies otherwise.
    return currentDay <= 1 ? 'connect' : 'message';
};

const defaultSubStrategyByStrategy = {
    [STRATEGY_IDS.BASELINE_NETWORKING]: 'warm-research-nurture',
    [STRATEGY_IDS.STRATEGIC_OUTREACH]: 'apply-then-message',
    [STRATEGY_IDS.REFERRAL_NETWORK]: 'advocate-seeding',
    [STRATEGY_IDS.ATS_FIRST]: 'same-day-strike'
};

const extractSignalStrength = (signal) => {
    return Number(signal?.hiring_signal_score || signal?.overallScore || signal?.signalScore || 0);
};

const normalizeLinkedinProfileUrl = (raw) => {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return '';
    try {
        return s.split('?')[0].replace(/\/$/, '');
    } catch {
        return '';
    }
};

/**
 * One stable key per human per org so we do not show two cards for the same person
 * (e.g. listed as both recruiter and team contact, or mismatched contactid vs providerId).
 */
const getDailyTaskPersonKey = (task) => {
    if (!task || task.type === 'ghost') {
        return `ghost::${String(task?.contactid || task?.name || 'ghost')}`;
    }
    const org = String(task._organizationId || '').trim();
    const pid = task.providerId != null ? String(task.providerId).trim() : '';
    if (pid) return `${org}::pid::${pid}`;
    const cid = task.contactid != null ? String(task.contactid).trim() : '';
    if (cid) return `${org}::cid::${cid}`;
    const oid = task._id != null ? String(task._id).trim() : '';
    if (oid) return `${org}::oid::${oid}`;
    const li = normalizeLinkedinProfileUrl(
        task.profile_url || task.linkedin_url || task.linkedinUrl || task.linkedin || task.linkedin_profile
    );
    if (li) return `${org}::li::${li}`;
    const email = String(task.email || task.work_email || '').toLowerCase().trim();
    if (email) return `${org}::em::${email}`;
    const name = String(task.name || '').toLowerCase().trim().replace(/\s+/g, ' ');
    const company = String(task._companyName || task.companyName || '').toLowerCase().trim().replace(/\s+/g, ' ');
    if (name) return `${org}::nm::${name}|${company}`;
    return `${org}::unknown`;
};

/** Job docs from API use `applyLink`; older code referenced `jobLink`. */
const resolveApplicationUrl = (jobDoc) => {
    if (!jobDoc || typeof jobDoc !== 'object') return '#';
    const raw =
        jobDoc.applyLink
        || jobDoc.jobLink
        || jobDoc.job_apply_link
        || jobDoc.url
        || jobDoc.link;
    const s = typeof raw === 'string' ? raw.trim() : '';
    return s && s !== '#' ? s : '#';
};

const getJobAgeHours = (job = {}) => {
    const rawDate = job?.datentime || job?.postedAt || job?.createdAt;
    if (!rawDate) return 48;

    const posted = new Date(rawDate);
    if (Number.isNaN(posted.getTime())) return 48;

    const diffMs = Date.now() - posted.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
};

/** Days since posted; null if unknown (do not treat as stale). */
const getJobPostingAgeDays = (job = {}) => {
    const rawDate = job?.datentime || job?.postedAt || job?.createdAt;
    if (!rawDate) return null;
    const posted = new Date(rawDate);
    if (Number.isNaN(posted.getTime())) return null;
    return Math.max(0, (Date.now() - posted.getTime()) / (1000 * 60 * 60 * 24));
};

/** Avoid “official apply” milestones for ancient listings (often closed on LinkedIn). */
const MAX_JOB_POSTING_AGE_DAYS_FOR_APPLY_MILESTONE = 90;

const shouldOfferOfficialApplyMilestone = (job = {}) => {
    if (job.isActive === false) return false;
    const ageDays = getJobPostingAgeDays(job);
    if (ageDays == null) return true;
    return ageDays <= MAX_JOB_POSTING_AGE_DAYS_FOR_APPLY_MILESTONE;
};

const STALE_POSTING_VERIFY_GHOST_OPTS = {
    applicationMilestone: true,
    stalePostingVerify: true,
    taskReason: 'This saved posting is older than ~3 months. Boards often stop accepting applications while the listing remains visible—confirm on LinkedIn or the company site before applying.'
};

const selectSubStrategyKey = ({ strategyId, job, signalStrength, recruiterCount, persistedKey }) => {
    if (persistedKey) return persistedKey;

    const ageHours = getJobAgeHours(job);
    const hasOpenRole = Boolean(job?.jobPosition || job?.jobTitle || job?._id);

    if (strategyId === STRATEGY_IDS.ATS_FIRST) {
        if (ageHours <= 12) return 'same-day-strike';
        if (ageHours <= 36) return '24h-escalation';
        return 'fast-fallback';
    }

    if (strategyId === STRATEGY_IDS.REFERRAL_NETWORK) {
        if (signalStrength >= 80 && recruiterCount >= 2) return 'advocate-seeding';
        if (signalStrength >= 70) return 'referral-with-proof';
        return 'fallback-to-outreach';
    }

    if (strategyId === STRATEGY_IDS.STRATEGIC_OUTREACH) {
        if (!hasOpenRole) return 'manager-first-outreach';
        if (signalStrength >= 70) return 'signal-boosted-outreach';
        return 'apply-then-message';
    }

    if (signalStrength >= 65) return 'monitor-and-pivot';
    return 'warm-research-nurture';
};

const buildJobDayTasks = ({
    strategyId,
    job,
    recruiters,
    recruitersWithEmail,
    others,
    ghostApply,
    companyName,
    companyLogo,
    jobTitle,
    signalStrength,
    selectedSubStrategy,
    opportunityScore,
    savedJobId,
    sprintId = null,
    includeApplyMilestone = true,
    outreachPlan = null,
    outreachPathId = null,
    outreachContacts = null,
    outreachPathTitle = null
}) => {
    const dayTasks = {};
    for (let i = 1; i <= SPRINT_DAYS; i++) dayTasks[i] = [];

    /**
     * Avoid repeating the *same outreach intent* on back-to-back days for one profile when the pool is tiny (cursor wraps).
     * Different intents are allowed — e.g. day 1 connect → day 2 message to the same contact A.
     */
    const personLastSlotByKey = new Map();

    const registerPersonScheduled = (contactRow, day, intent) => {
        if (!contactRow || contactRow.type === 'ghost') return;
        const key = getDailyTaskPersonKey(contactRow);
        if (key.startsWith('ghost::')) return;
        personLastSlotByKey.set(key, { day, intent });
    };

    const wouldRepeatSameIntentPreviousDay = (contactRow, day, intent) => {
        if (!contactRow || contactRow.type === 'ghost') return false;
        const key = getDailyTaskPersonKey(contactRow);
        if (key.startsWith('ghost::')) return false;
        const prev = personLastSlotByKey.get(key);
        return Boolean(prev && prev.day === day - 1 && prev.intent === intent);
    };

    const cursor = { recruiter: 0, other: 0 };
    const hasOpenRole = Boolean(job?.jobPosition || job?.jobTitle || job?._id);

    const getRoleLabel = (roleKey) => {
        if (roleKey === 'hiringManagers') return 'Hiring Manager';
        if (roleKey === 'others') return 'Team Contact';
        return 'Recruiter';
    };

    const addGhost = (day, name, code, link = '#', ghostOptions = {}) => {
        const suggestedRoleKey = strategyId === STRATEGY_IDS.REFERRAL_NETWORK
            ? 'others'
            : strategyId === STRATEGY_IDS.BASELINE_NETWORKING
                ? 'recruiters'
                : 'recruiters';
        const suggested = pickAnyAvailableContact({ requireEmail: false });

        const standaloneMilestone = code === 'apply' || code === 'stale_posting_verify';
        // Even when we have zero contacts for this org (e.g. orgId not enriched yet),
        // we still want to show a “ghost” task so the Daily Tasks board isn't blank.

        const resolvedRoleKey = suggested.contact ? suggested.pool : suggestedRoleKey;
        const resolvedTitle = suggested.contact?.title || getRoleLabel(resolvedRoleKey);

        const isApplicationRow = code === 'apply' || Boolean(ghostOptions.applicationMilestone);

        dayTasks[day].push({
            contactid: `ghost_${savedJobId}_${day}_${code}`,
            name,
            type: 'ghost',
            // Ghosts need the sprint id too: the board filters by _sprintId and
            // completion is keyed by it — without it milestones vanish from
            // sprint-filtered views.
            _sprintId: sprintId,
            _isApplication: isApplicationRow,
            _stalePostingVerify: Boolean(ghostOptions.stalePostingVerify),
            _taskReason: typeof ghostOptions.taskReason === 'string' ? ghostOptions.taskReason : undefined,
            _link: link,
            _companyName: companyName,
            _companyLogo: companyLogo,
            _jobTitle: jobTitle,
            _baseJobId: job?._id || job?.id || job?.jobId || null,
            _opportunityScore: opportunityScore + 1,
            title: isApplicationRow ? 'Core Milestone' : resolvedTitle,
            _suggestedRoleKey: resolvedRoleKey,
            _suggestedContact: suggested.contact || null,
            _taskName: code === 'apply' ? name : name
        });
    };

    const pickContactFromPool = (poolName, options = {}) => {
        const requireEmail = Boolean(options.requireEmail);
        const list = poolName === 'recruiter'
            ? (requireEmail ? recruitersWithEmail : recruiters)
            : others;

        if (!list.length) return null;

        const index = cursor[poolName] || 0;
        const contact = list[index % list.length];
        cursor[poolName] = index + 1;
        return contact;
    };

    const pickAnyAvailableContact = (options = {}) => {
        const primaryRecruiter = pickContactFromPool('recruiter', options);
        if (primaryRecruiter) return { contact: primaryRecruiter, pool: 'recruiter' };

        const fallbackOther = pickContactFromPool('other', options);
        if (fallbackOther) return { contact: fallbackOther, pool: 'other' };

        const fallbackRecruiterNoEmail = options.requireEmail ? pickContactFromPool('recruiter', { requireEmail: false }) : null;
        if (fallbackRecruiterNoEmail) return { contact: fallbackRecruiterNoEmail, pool: 'recruiter' };

        const fallbackOtherNoEmail = options.requireEmail ? pickContactFromPool('other', { requireEmail: false }) : null;
        if (fallbackOtherNoEmail) return { contact: fallbackOtherNoEmail, pool: 'other' };

        return { contact: null, pool: null };
    };

    const pickContactForPool = (poolName, options = {}) => {
        const targetPool = poolName === 'other' ? 'other' : 'recruiter';
        const primary = pickContactFromPool(targetPool, options);
        if (primary) return { contact: primary, pool: targetPool };
        return pickAnyAvailableContact(options);
    };

    const addContact = (day, pool, fallbackGhostName, options = {}) => {
        const requireEmail = Boolean(options.requireEmail);
        const poolSize = Math.max(recruiters.length + others.length, 1);
        const maxAttempts = Math.min(poolSize * 4, 48);

        const slotIntent = inferMissionIntent(day, '');

        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
            const selected = pickContactForPool(pool, { requireEmail });
            const contact = selected.contact;

            if (!contact) {
                addGhost(day, fallbackGhostName, `fallback_${pool}_${day}`);
                return;
            }

            if (wouldRepeatSameIntentPreviousDay(contact, day, slotIntent)) {
                continue;
            }

            const personKey = getDailyTaskPersonKey(contact);
            const alreadyScheduled = dayTasks[day].some((task) => getDailyTaskPersonKey(task) === personKey);
            if (alreadyScheduled) return;

            dayTasks[day].push({
                ...contact,
                title: selected.pool === 'recruiter' ? (contact.title || 'Recruiter') : (contact.title || 'Hiring Manager')
            });
            registerPersonScheduled(contact, day, slotIntent);
            return;
        }

        addGhost(day, fallbackGhostName, `fallback_${pool}_${day}`);
    };

    const attachActionToContactTask = (day, actionText, actionCode) => {
        const existingContactTask = dayTasks[day].find((item) => item?.type !== 'ghost');
        if (!existingContactTask) {
            const selected = pickAnyAvailableContact();
            if (!selected.contact) {
                return;
            }

            if (dayTasks[day].some((t) => getDailyTaskPersonKey(t) === getDailyTaskPersonKey(selected.contact))) {
                return;
            }

            const actionIntent = inferMissionIntent(day, actionText);
            if (wouldRepeatSameIntentPreviousDay(selected.contact, day, actionIntent)) {
                return;
            }

            dayTasks[day].push({
                ...selected.contact,
                title: selected.pool === 'recruiter'
                    ? (selected.contact.title || 'Recruiter')
                    : (selected.contact.title || 'Hiring Manager'),
                _taskName: actionText
            });
            registerPersonScheduled(selected.contact, day, actionIntent);
            return;
        }

        if (!existingContactTask._taskName) {
            existingContactTask._taskName = actionText;
        }
    };

    const scheduleSubStrategyActions = () => {
        const actions = selectedSubStrategy?.actions || [];
        if (!actions.length) return;

        const actionDays = [1, 3, 5, 8, 11, 14];
        actionDays.forEach((day, idx) => {
            const actionText = actions[idx % actions.length];
            attachActionToContactTask(day, actionText, idx + 1);
        });
    };

    // Outreach-activated job: render its exact day-by-day plan (one task per day
    // with the strategy's verbatim action text). The task carries the chosen
    // "who to contact" people so the card can list them with a channel-aware
    // (Gmail / LinkedIn) action. Falls back to saved pools, then a ghost.
    if (Array.isArray(outreachPlan) && outreachPlan.length) {
        const preferOther = outreachPathId === 'referral';
        const people = Array.isArray(outreachContacts) ? outreachContacts.filter(Boolean) : [];
        outreachPlan.forEach((step, idx) => {
            const day = idx + 1;
            if (!dayTasks[day]) dayTasks[day] = [];
            const action = step?.action || step?.label || 'Outreach touch';
            // Primary contact (drives completion id); the card shows the full list.
            const primary = people.length
                ? people[idx % people.length]
                : (preferOther ? pickContactForPool('other') : pickContactForPool('recruiter')).contact;

            if (primary) {
                dayTasks[day].push({
                    ...primary,
                    title: primary.title || (preferOther ? 'Team Contact' : 'Recruiter'),
                    _taskName: action,
                    _dayChannel: step?.channel || null,
                    _dayGoal: step?.goal || null,
                    _pathTitle: outreachPathTitle || null,
                    _outreachContacts: people
                });
            } else {
                addGhost(day, action, `outreach_${day}`);
            }
        });
        return dayTasks;
    }

    if (strategyId === STRATEGY_IDS.BASELINE_NETWORKING) {
        for (let day = 1; day <= SPRINT_DAYS; day++) {
            addContact(day, 'recruiter', day === 4 || day === 6
                ? 'Use suggested recruiter contact for email outreach'
                : 'Use suggested recruiter contact for outreach', { requireEmail: day === 4 || day === 6 });
            if (day % 2 === 0 && day !== 4 && day !== 6) addContact(day, 'other', 'Use suggested hiring team contact');
            if (day === 1 || day === 7 || day === 14) addGhost(day, 'Review hiring signals and update target contacts', `monitor_${day}`);
        }
    } else if (strategyId === STRATEGY_IDS.STRATEGIC_OUTREACH) {
        for (let day = 1; day <= SPRINT_DAYS; day++) {
            if (day === 1 && hasOpenRole) {
                if (includeApplyMilestone) addGhost(day, ghostApply.name, 'apply', ghostApply._link);
                else addGhost(day, 'Confirm role is still open before applying', 'stale_posting_verify', ghostApply._link, STALE_POSTING_VERIFY_GHOST_OPTS);
            }
            addContact(day, 'recruiter', day === 4 || day === 6
                ? 'Use suggested recruiter email and outreach angle'
                : 'Use suggested recruiter outreach angle', { requireEmail: day === 4 || day === 6 });
            if ((day <= 5 || day % 2 === 0) && day !== 4 && day !== 6) addContact(day, 'other', 'Use suggested hiring manager and craft value message');
            if (day === 4 || day === 8 || day === 12) addGhost(day, 'Send concise follow-up with role-fit proof points', `followup_${day}`);
        }
    } else if (strategyId === STRATEGY_IDS.REFERRAL_NETWORK) {
        for (let day = 1; day <= SPRINT_DAYS; day++) {
            if (day === 4 && hasOpenRole) {
                if (includeApplyMilestone) addGhost(day, ghostApply.name, 'apply', ghostApply._link);
                else addGhost(day, 'Confirm role is still open before applying', 'stale_posting_verify', ghostApply._link, STALE_POSTING_VERIFY_GHOST_OPTS);
            }
            addContact(day, 'recruiter', day === 4 || day === 6
                ? 'Expand referral path through suggested recruiter touchpoint'
                : 'Expand referral path through suggested recruiter touchpoint', { requireEmail: day === 4 || day === 6 });
            if (day !== 4 && day !== 6) addContact(day, 'other', 'Build rapport with suggested internal advocate');
            if (day === 6 || day === 10) addGhost(day, 'Request referral with concise proof of fit', `referral_${day}`);
            if (day === 12) addGhost(day, 'If referral path stalls, pivot to direct outreach plan', 'fallback_outreach');
        }
    } else {
        for (let day = 1; day <= SPRINT_DAYS; day++) {
            if (day === 1 && hasOpenRole) {
                if (includeApplyMilestone) addGhost(day, ghostApply.name, 'apply', ghostApply._link);
                else addGhost(day, 'Confirm role is still open before applying', 'stale_posting_verify', ghostApply._link, STALE_POSTING_VERIFY_GHOST_OPTS);
            }
            addContact(day, 'recruiter', 'Immediate same-day recruiter outreach', { requireEmail: day === 4 || day === 6 });
            if (day <= 7 || day % 2 === 0) addContact(day, 'recruiter', 'Fast follow-up on ATS-first application', { requireEmail: day === 4 || day === 6 });
            if (day <= 5 && day !== 4 && day !== 6) addContact(day, 'other', 'Escalate to alternate internal contact for speed');
            if (day === 2 || day === 3 || day === 6) addGhost(day, 'Escalate follow-up due to ATS-first urgency', `ats_escalate_${day}`);
        }
    }

    scheduleSubStrategyActions();

    if (signalStrength >= 70) {
        addGhost(3, 'High signal detected: prioritize warm intros and immediate follow-up', 'signal_boost_3');
        addGhost(9, 'Re-check signal momentum and escalate strongest path', 'signal_boost_9');
    }

    if (recruiters.length < 2) {
        addGhost(1, 'Low recruiter coverage: source two additional recruiter contacts', 'low_recruiter_1');
        addGhost(2, 'Backfill recruiter pipeline from company employees list', 'low_recruiter_2');
    }

    if (!hasOpenRole) {
        addGhost(2, 'No open role yet: monitor company hiring pages and set alerts', 'no_role_2');
        addGhost(6, 'Prepare role-tailored resume variants for upcoming openings', 'no_role_6');
    }

    const mergeNonApplicationGhostsIntoContacts = () => {
        for (let day = 1; day <= SPRINT_DAYS; day++) {
            const tasks = dayTasks[day] || [];
            if (!tasks.length) continue;

            const contactTasks = tasks.filter((item) => item?.type !== 'ghost');
            if (!contactTasks.length) continue;

            const nextDayTasks = [];
            let contactCursor = 0;

            tasks.forEach((task) => {
                if (task?.type !== 'ghost') {
                    nextDayTasks.push(task);
                    return;
                }

                if (task?._isApplication) {
                    nextDayTasks.push(task);
                    return;
                }

                const targetContact = contactTasks[contactCursor % contactTasks.length];
                contactCursor += 1;

                if (!targetContact._taskName) {
                    targetContact._taskName = task.name;
                    return;
                }

                if (!targetContact._taskName.includes(task.name)) {
                    targetContact._taskName = `${targetContact._taskName} | ${task.name}`;
                }
            });

            dayTasks[day] = nextDayTasks;
        }
    };

    mergeNonApplicationGhostsIntoContacts();

    return dayTasks;
};

const DailyTasks = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector(state => state.auth.user);
    const savedJobs = useSelector(state => state.savedJobs.savedJobs);
    const savedCompanies = useSelector(state => state.savedCompanies.savedCompanies);
    const activeSprints = useSelector(state => state.activeSprints.sprints) || [];
    const activeSprintsLoading = useSelector(state => state.activeSprints.loading);
    const activeSprintsFetched = useSelector(state => state.activeSprints.fetched);

    // ── F-02: Single useSelector for the entire dailyTasks slice ──
    // Previously two separate calls caused two subscriptions and potential double renders.
    const {
        linkedinDone,
        firstEmailDone,
        dmDone,
        followupEmailDone,
        doneKeys,
        taskHistoryByContact
    } = useSelector(state => state.dailyTasks);

    const { perplexitySignals } = useSelector(state => state.hsignal);
    const hsignalLoading = useSelector(state => state.hsignal.loading);
    const hsignalError = useSelector(state => state.hsignal.error);
    const savedJobsLoading = useSelector(state => state.savedJobs.savedJobsLoading);
    const { contactsByCompanyId } = useSelector(state => state.contacts) ?? EMPTY_OBJ;
    const liauthState = useSelector(state => state.liauth) ?? EMPTY_OBJ;
    const inviteStatusesByProviderId = liauthState.inviteStatusesByProviderId ?? EMPTY_OBJ;
    const inviteStatusesByContactId = liauthState.inviteStatusesByContactId ?? EMPTY_OBJ;
    const isLinkedInConnected = liauthState.isLinkedInConnected;
    // ── F-11: Use stable EMPTY_OBJ sentinel instead of inline `|| {}` ──
    const companyStrategyByOrganizationId = useSelector(state => state.companyStrategy.byOrganizationId) ?? EMPTY_OBJ;

    // companyFilter holds the selected sprint id (or 'all' before the default
    // sprint is chosen). Day pointer and completed-days are BOTH per sprint —
    // each sprint keeps its own place and its own progress.
    const [companyFilter, setCompanyFilter] = useState('all');
    const selectedSprintKey = String(companyFilter);

    // Explicit day picks per sprint. `currentDay` itself is derived further down
    // (after campaigns): a sprint with no explicit pick opens on its highest
    // unlocked day, not Day 1.
    const [dayBySprint, setDayBySprint] = useState({});
    const setCurrentDay = useCallback((day) => {
        setDayBySprint((prev) => (prev[selectedSprintKey] === day ? prev : { ...prev, [selectedSprintKey]: day }));
    }, [selectedSprintKey]);

    // Steps the user chose to open before their scheduled date (soft gate:
    // weeks unlock on schedule automatically, or early via the banner action).
    // NOTE: completed days are NOT stored — they derive from task completion
    // data (the DB-backed timeline), so they're always right on any device.
    const [earlyUnlocksBySprint, setEarlyUnlocksBySprint] = useState({});

    const fetchedCompanyIdsRef = useRef(new Set());
    const [dismissedRecommendations, setDismissedRecommendations] = useState({});
    const [updatingStrategyJobId, setUpdatingStrategyJobId] = useState(null);

    const announcedSuggestionKeys = useRef(new Set());

    const authUserId = user?._id || user?.id;
    const [signalsFetchStarted, setSignalsFetchStarted] = useState(false);

    // Load user-scoped board state once the user id is known; persist only after
    // the load ran so the initial empty state can't clobber stored progress.
    const boardStateLoadedRef = useRef(false);
    useEffect(() => {
        if (!authUserId) return;
        try {
            const savedEarly = localStorage.getItem(`earlyUnlocks:${authUserId}`);
            if (savedEarly) setEarlyUnlocksBySprint(JSON.parse(savedEarly));
            const savedDismissed = localStorage.getItem(`dismissedStrategyRecommendations:${authUserId}`);
            if (savedDismissed) setDismissedRecommendations(JSON.parse(savedDismissed));
        } catch {
            // Corrupt stored state — start clean.
        }
        boardStateLoadedRef.current = true;
    }, [authUserId]);

    // ── F-01: Only dispatch fetchTaskTimeline ──
    // BEFORE: 9 API calls on mount including 4 redundant fetch-done calls
    // AFTER:  5 API calls — fetchTaskTimeline covers all task completion data
    // Use id OR _id — login payload uses `id`; getUser profile uses `_id`.
    useEffect(() => {
        if (!authUserId) return;
        dispatch(fetchTaskTimeline(authUserId));
        dispatch(fetchPerplexitySignals());
        setSignalsFetchStarted(true);
        dispatch(fetchSavedCompanies(authUserId));
        dispatch(fetchCompanies());
        // fetchActiveSprints intentionally NOT here — the location-scoped effect
        // below fires on mount too; dispatching in both doubled the request.
    }, [dispatch, authUserId]);

    // Refresh sprints whenever this screen is (re)entered (e.g. right after activating).
    useEffect(() => {
        if (authUserId && location.pathname === '/dailytasks') dispatch(fetchActiveSprints(authUserId));
    }, [dispatch, authUserId, location.pathname]);

    /** Saved missions + activeStrategy — always refresh on this screen so deploy/strategy updates show up */
    useEffect(() => {
        if (!authUserId || location.pathname !== '/dailytasks') return;
        dispatch(fetchSavedJobs(authUserId));
    }, [dispatch, authUserId, location.pathname]);

    useEffect(() => {
        if (!authUserId || !boardStateLoadedRef.current) return;
        localStorage.setItem(`earlyUnlocks:${authUserId}`, JSON.stringify(earlyUnlocksBySprint));
    }, [earlyUnlocksBySprint, authUserId]);

    useEffect(() => {
        if (!authUserId || !boardStateLoadedRef.current) return;
        localStorage.setItem(`dismissedStrategyRecommendations:${authUserId}`, JSON.stringify(dismissedRecommendations));
    }, [dismissedRecommendations, authUserId]);

    // --- STRATEGY & SPRINT DATA ---
    
    // Find all jobs ready for a strategy
    /** Mongo/API may send activeStrategy as string; buildJobDayTasks uses strict === on STRATEGY_IDS numbers. */
    const normalizeSavedJobStrategyId = (job) => {
        const n = Number(job?.activeStrategy);
        return Number.isFinite(n) && n >= 1 && n <= 4 ? n : null;
    };

    // Each ActiveSprint → a pseudo "saved job" the existing day-building pipeline
    // understands. One sprint = one row (a role can have several: referral +
    // direct). The snapshot mirrors what getOutreachPlan/Contacts/PathTitle read.
    const activeSprintJobs = useMemo(() => {
        return (activeSprints || []).map((sp) => ({
            _id: sp._id,
            _sprintId: sp._id,
            _activatedAt: sp.createdAt || null, // drives time-gating of week-cadence steps
            _pathId: sp.pathId,
            _pathTitle: sp.pathTitle,
            _strategyName: sp.strategyName || null,
            organizationId: sp.organizationId || null,
            activeStrategy: 1, // dummy (1–4) so legacy guards treat it as an active sprint
            activeSubStrategyKey: sp.subStrategyKey || null,
            strategyContextSnapshot: {
                source: 'outreach-modal',
                pathId: sp.pathId,
                plan: Array.isArray(sp.plan) ? sp.plan : [],
                contacts: Array.isArray(sp.contacts) ? sp.contacts : []
            },
            jobId: {
                _id: sp.sourceId,
                companyName: sp.companyName,
                jobPosition: sp.jobTitle,
                companyLogo: sp.companyLogo,
                companyId: { companyLogo: sp.companyLogo }
            },
            _priorityScore: 0
        }));
    }, [activeSprints]);

    // Top priority job for the header
    const topPriorityJob = activeSprintJobs[0] || null;

    const signalIndex = useMemo(() => {
        const byOrg = {};
        const byName = {};

        (perplexitySignals || []).forEach((signal) => {
            const orgId = signal?.organizationId || signal?.organization_id || signal?.companyId || signal?.company_id;
            const companyName = (signal?.companyName || signal?.company_name || signal?.company || '').toLowerCase();

            if (orgId) byOrg[orgId] = signal;
            if (companyName) byName[companyName] = signal;
        });

        return { byOrg, byName };
    }, [perplexitySignals]);

    const strategySuggestions = useMemo(() => {
        if (!activeSprintJobs.length) return [];

        const normalizeStrategyId = (value) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };

        const subStrategyToStrategyId = STRATEGY_CATALOG.reduce((acc, strategy) => {
            (strategy.subStrategies || []).forEach((sub) => {
                if (sub?.key) acc[sub.key] = strategy.id;
            });
            return acc;
        }, {});

        return activeSprintJobs
            .map((job) => {
                const companyName = (job?.jobId?.companyName || '').toLowerCase();
                const signal = signalIndex.byOrg[job.organizationId] || signalIndex.byName[companyName] || null;
                // ── F-03: Use stable EMPTY_CONTACTS sentinel instead of inline `|| {}` ──
                const companyContacts = contactsByCompanyId[job.organizationId] ?? EMPTY_CONTACTS;
                const recruiterCount = (companyContacts?.recruiterContacts || []).length + (companyContacts?.otherContacts || []).length;
                const recommendation = recommendStrategy({
                    job: job?.jobId || {},
                    signal,
                    userProfile: user || {},
                    context: { recruiterCount }
                });

                const jobStrategyId = normalizeStrategyId(job?.activeStrategy);
                const inferredStrategyIdFromSubKey = normalizeStrategyId(
                    subStrategyToStrategyId[job?.activeSubStrategyKey]
                );
                const companyStrategyId = normalizeStrategyId(companyStrategyByOrganizationId?.[job.organizationId]?.strategyId);
                const effectiveCurrentStrategyId = companyStrategyId || inferredStrategyIdFromSubKey || jobStrategyId;
                const recommendedStrategyId = normalizeStrategyId(recommendation?.strategyId);

                if (!recommendedStrategyId || recommendedStrategyId === effectiveCurrentStrategyId) {
                    return null;
                }

                const dismissKey = `${job._id}-${recommendedStrategyId}`;
                if (dismissedRecommendations[dismissKey]) {
                    return null;
                }

                return {
                    savedJobId: job._id,
                    jobId: job?.jobId?._id || job?.jobId,
                    organizationId: job.organizationId,
                    companyName: job?.jobId?.companyName || 'Unknown Company',
                    currentStrategyId: effectiveCurrentStrategyId,
                    recommendedStrategyId,
                    confidence: recommendation.confidence,
                    reason: recommendation.reason
                };
            })
            .filter(Boolean)
            .sort((a, b) => b.confidence - a.confidence);
            }, [activeSprintJobs, signalIndex, dismissedRecommendations, companyStrategyByOrganizationId, contactsByCompanyId, user]);

    // Prevent “flash then disappear” during initial signal fetch / hydration.
    // Gate suggestions until the signals request has actually started and finished.
    const signalsReady = signalsFetchStarted && !hsignalLoading;
    const strategyInputsReady = signalsReady && !savedJobsLoading;
    const [stableStrategySuggestions, setStableStrategySuggestions] = useState([]);
    const suggestionsTimerRef = useRef(null);
    const strategyReadyAtRef = useRef(0);

    useEffect(() => {
        if (suggestionsTimerRef.current) {
            clearTimeout(suggestionsTimerRef.current);
            suggestionsTimerRef.current = null;
        }

        // Keep prior stable list until signals are ready (or errored).
        if (!strategyInputsReady && !hsignalError) return undefined;

        // Record first time we reach a “ready” state.
        if (strategyReadyAtRef.current === 0 && strategyInputsReady) {
            strategyReadyAtRef.current = Date.now();
        }

        suggestionsTimerRef.current = setTimeout(() => {
            const warmupMs = 1500;
            const readyForMs = strategyReadyAtRef.current ? (Date.now() - strategyReadyAtRef.current) : 0;
            const next = Array.isArray(strategySuggestions) ? strategySuggestions : [];

            // Avoid flicker: during warmup, don't replace a non-empty stable list with empty.
            if (readyForMs < warmupMs && stableStrategySuggestions.length > 0 && next.length === 0) {
                return;
            }

            setStableStrategySuggestions(next);
        }, 450);

        return () => {
            if (suggestionsTimerRef.current) {
                clearTimeout(suggestionsTimerRef.current);
                suggestionsTimerRef.current = null;
            }
        };
    }, [strategySuggestions, strategyInputsReady, hsignalError, stableStrategySuggestions.length]);

    const handleAcceptRecommendation = async (suggestion) => {
        const uid = user?._id || user?.id;
        if (!uid || !suggestion?.jobId) return;

        setUpdatingStrategyJobId(suggestion.savedJobId);
        try {
            await dispatch(selectJobStrategy({
                userId: uid,
                jobId: suggestion.jobId,
                strategyId: suggestion.recommendedStrategyId
            })).unwrap();

            await dispatch(fetchSavedJobs(uid));

            if (suggestion.organizationId) {
                dispatch(setCompanyStrategy({
                    organizationId: suggestion.organizationId,
                    strategyId: suggestion.recommendedStrategyId,
                    selectedByUser: true,
                    reason: suggestion.reason
                }));

                dispatch(dismissCompanyRecommendation({
                    organizationId: suggestion.organizationId
                }));
            }
        } catch (error) {
            console.error('Failed to update strategy from recommendation:', error);
        } finally {
            setUpdatingStrategyJobId(null);
        }
    };

    const handleDismissRecommendation = (suggestion) => {
        const dismissKey = `${suggestion.savedJobId}-${suggestion.recommendedStrategyId}`;
        setDismissedRecommendations((prev) => ({ ...prev, [dismissKey]: true }));

        if (suggestion.organizationId) {
            dispatch(dismissCompanyRecommendation({
                organizationId: suggestion.organizationId
            }));
        }
    };

    useEffect(() => {
        strategySuggestions.forEach((suggestion) => {
            if (!suggestion.organizationId) return;

            // One announcement per org+strategy: the recommendation content is
            // identical on recomputes, so re-dispatching it every time the memo
            // inputs changed was pure store churn.
            const suggestionKey = `${suggestion.organizationId}-${suggestion.recommendedStrategyId}`;
            if (announcedSuggestionKeys.current.has(suggestionKey)) return;
            announcedSuggestionKeys.current.add(suggestionKey);

            dispatch(setCompanyRecommendation({
                organizationId: suggestion.organizationId,
                strategyId: suggestion.recommendedStrategyId,
                confidence: suggestion.confidence,
                reason: suggestion.reason
            }));

            dispatch(pushStrategyChangeEvent({
                organizationId: suggestion.organizationId,
                type: 'recommended',
                previousStrategyId: suggestion.currentStrategyId,
                recommendedStrategyId: suggestion.recommendedStrategyId,
                confidence: suggestion.confidence,
                reason: suggestion.reason,
                source: 'hiring-signal'
            }));
        });
    }, [strategySuggestions, dispatch]);

    // Fetch contacts for all active companies (ref, not state: dedupe bookkeeping
    // shouldn't trigger extra renders or re-run this effect).
    useEffect(() => {
        const orgIds = new Set(activeSprintJobs.map(j => j.organizationId).filter(Boolean));

        orgIds.forEach(orgId => {
            if (!fetchedCompanyIdsRef.current.has(orgId)) {
                fetchedCompanyIdsRef.current.add(orgId);
                dispatch(fetchAllContactsByCompanyId(orgId));
            }
        });
    }, [dispatch, activeSprintJobs]);

    // --- SPRINT SCHEDULER (AGGREGATED & PRIORITIZED) ---

    const contactsByDay = useMemo(() => {
        const days = {};
        for (let i = 1; i <= SPRINT_DAYS; i++) days[i] = [];

        if (activeSprintJobs.length === 0) return days;

        activeSprintJobs.forEach(job => {
            const orgId = job.organizationId;
            const strategyId = job.activeStrategy;
            const contactData = contactsByCompanyId?.[orgId] ?? EMPTY_CONTACTS;
            const companyNameKey = (job?.jobId?.companyName || '').toLowerCase();
            const signal = signalIndex.byOrg[orgId] || signalIndex.byName[companyNameKey] || null;
            const signalStrength = extractSignalStrength(signal);
            
            const matchingCompany = savedCompanies.find(c => c.organizationId === orgId);
            const contextCompanyName = job.jobId?.companyName || matchingCompany?.companyName || 'Unknown Company';
            const contextJobTitle = job.jobId?.jobPosition || '';
            const contextJobDescription = job.jobId?.jobDescription || job.jobId?.description || '';
            const contextCompanyLogo = job.jobId?.companyId?.companyLogo || matchingCompany?.companyLogo || '';
            const applicationUrl = resolveApplicationUrl(job.jobId);

            const attachContext = (contact, priority) => ({
                ...contact,
                _companyName: contextCompanyName,
                _companyLogo: contextCompanyLogo,
                _jobTitle: contextJobTitle,
                _jobDescription: contextJobDescription,
                _baseJobId: job?.jobId?._id || job?.jobId || null,
                _organizationId: orgId,
                _strategyId: strategyId,
                _sprintId: job?._sprintId || null,
                _pathTitle: job?._pathTitle || null,
                _priority: priority,
                _opportunityScore: job._priorityScore
            });

            // Ghost Task Creation (Static goals that don't need contact data)
            const ghostApply = {
                contactid: `ghost_apply_${job._id}`,
                name: "Submit Official Application",
                type: 'ghost',
                _isApplication: true,
                _link: applicationUrl,
                _companyName: contextCompanyName,
                _companyLogo: contextCompanyLogo,
                _jobTitle: contextJobTitle,
                _baseJobId: job?.jobId?._id || job?.jobId || null,
                _sprintId: job?._sprintId || null,
                _opportunityScore: job._priorityScore + 2,
                title: "Core Milestone"
            };

            const recruiters = (contactData?.recruiterContacts || []).map(c => attachContext(c, 'high'));
            const recruitersWithEmail = recruiters.filter((contact) => Boolean(contact?.email));
            const others = (contactData?.otherContacts || []).map(c => attachContext(c, 'medium'));

            // The exact who-to-contact people chosen at activation (context-attached
            // so the email popup + completion have company/job info).
            const outreachContacts = getOutreachContacts(job).map(c =>
                attachContext(c, c?.predicted_role === 'recruiter' ? 'high' : 'medium')
            );

            const strategyDefinition = STRATEGY_CATALOG.find((strategy) => strategy.id === strategyId) || null;
            const selectedSubStrategyKey = selectSubStrategyKey({
                strategyId,
                job: job?.jobId || {},
                signalStrength,
                recruiterCount: recruiters.length,
                persistedKey: job?.activeSubStrategyKey || job?.strategyContextSnapshot?.subStrategyKey || null
            });

            const selectedSubStrategy = strategyDefinition?.subStrategies?.find(
                (sub) => sub.key === selectedSubStrategyKey
            ) || strategyDefinition?.subStrategies?.[0] || {
                key: defaultSubStrategyByStrategy[strategyId],
                actions: strategyDefinition?.steps || []
            };

            const jobTasks = buildJobDayTasks({
                strategyId,
                job: job?.jobId || {},
                recruiters,
                recruitersWithEmail,
                others,
                ghostApply,
                companyName: contextCompanyName,
                companyLogo: contextCompanyLogo,
                jobTitle: contextJobTitle,
                signalStrength,
                selectedSubStrategy,
                opportunityScore: job._priorityScore,
                savedJobId: job._id,
                sprintId: job?._sprintId || null,
                includeApplyMilestone: shouldOfferOfficialApplyMilestone(job?.jobId || {}),
                outreachPlan: getOutreachPlan(job),
                outreachPathId: job?.strategyContextSnapshot?.pathId || null,
                outreachContacts,
                outreachPathTitle: getOutreachPathTitle(job)
            });

            // Merge into days numerically to avoid string key issues
            Object.keys(jobTasks).forEach(dayStr => {
                const dayNum = parseInt(dayStr);
                if (days[dayNum]) {
                    days[dayNum] = [...days[dayNum], ...jobTasks[dayStr]];
                }
            });
        });

        // PRIORITIZE each day's list
        for (let i = 1; i <= SPRINT_DAYS; i++) {
            const dedupedTasks = [];
            const taskByKey = new Map();

            (days[i] || []).forEach((task) => {
                // Key by sprint + person so the same contact can appear in both the
                // referral and direct sprints on the same day without collapsing.
                const key = `${task?._sprintId || 'x'}::${getDailyTaskPersonKey(task)}`;
                const existing = taskByKey.get(key);

                if (!existing) {
                    taskByKey.set(key, task);
                    dedupedTasks.push(task);
                    return;
                }

                if (task?.type !== 'ghost' && existing?.type !== 'ghost') {
                    const existingTaskName = String(existing?._taskName || '').trim();
                    const nextTaskName = String(task?._taskName || '').trim();
                    if (!existingTaskName && nextTaskName) {
                        existing._taskName = nextTaskName;
                    } else if (existingTaskName && nextTaskName && !existingTaskName.includes(nextTaskName)) {
                        existing._taskName = `${existingTaskName} | ${nextTaskName}`;
                    }
                }
            });

            days[i] = dedupedTasks;
            days[i].sort((a, b) => (b._opportunityScore || 0) - (a._opportunityScore || 0));
        }

        return days;
    }, [activeSprintJobs, contactsByCompanyId, savedCompanies, signalIndex]);

    const doneKeySet = useMemo(() => new Set(doneKeys), [doneKeys]);

    /**
     * Single source of truth for "is this task done".
     * Primary check: the sprint-scoped key (sprintId::stepIndex::contactId), so
     * the same contact scheduled in two sprints — or twice in one sprint —
     * completes independently. The legacy per-slot contactId lists remain as a
     * fallback only for records written before sprint scoping existed (and for
     * marks made from the Networking page, which are not sprint-bound).
     */
    const isMissionDoneForDay = useCallback((mission, day) => {
        const rawId = mission?.contactid || mission?._id;
        if (rawId == null || rawId === '') return false;
        const cid = String(rawId);
        if (mission?._sprintId && doneKeySet.has(makeDoneKey(mission._sprintId, day, cid))) return true;
        if (day === 4) return firstEmailDone.includes(cid);
        if (day === 5) return dmDone.includes(cid);
        if (day === 6) return followupEmailDone.includes(cid);
        return linkedinDone.includes(cid);
    }, [doneKeySet, firstEmailDone, dmDone, followupEmailDone, linkedinDone]);

    /** Is a given day of a given sprint fully done? Derived from task data only. */
    const isSprintDayComplete = useCallback((sprintKey, day) => {
        const tasks = (contactsByDay[day] || []).filter((t) => String(t._sprintId) === String(sprintKey));
        if (!tasks.length) return true; // nothing scheduled — trivially complete
        return tasks.every((t) => isMissionDoneForDay(t, day));
    }, [contactsByDay, isMissionDoneForDay]);

    /**
     * Highest unlocked step for ANY sprint: previous step complete AND (for
     * week-cadence signal sprints) the step's scheduled date reached, unless the
     * user opened it early. Used both for the selected sprint's gating and so
     * every sprint opens on its furthest actionable step instead of Day 1.
     */
    const highestUnlockedDayFor = useCallback((job) => {
        if (!job) return 1;
        const key = String(job._sprintId);
        const plan = getOutreachPlan(job);
        const total = plan?.length || SPRINT_DAYS;
        const isWeekCadence = job._pathId === 'signal';
        const activatedAtRaw = job._activatedAt;
        const activatedAt = activatedAtRaw ? new Date(activatedAtRaw) : null;
        const earlyUnlocks = earlyUnlocksBySprint[key] || [];

        const stepTimeLocked = (step) => {
            if (!isWeekCadence || earlyUnlocks.includes(step)) return false;
            if (!activatedAt || Number.isNaN(activatedAt.getTime())) return false;
            const m = /week\s*(\d+)/i.exec(String(plan?.[step - 1]?.label || ''));
            if (!m) return false;
            return Date.now() < activatedAt.getTime() + Number(m[1]) * 7 * 24 * 60 * 60 * 1000;
        };

        let h = 1;
        for (let d = 2; d <= total; d++) {
            if (isSprintDayComplete(key, d - 1) && !stepTimeLocked(d)) h = d;
            else break;
        }
        return h;
    }, [isSprintDayComplete, earlyUnlocksBySprint]);

    // ── v3 sprint workspace: campaign switcher ──
    const [campaignKindFilter, setCampaignKindFilter] = useState('all'); // all | job | signal | saved | unsaved
    // Sprint pending removal (drives the delete-confirmation modal).
    const [pendingRemove, setPendingRemove] = useState(null);
    const [campaignSearch, setCampaignSearch] = useState('');

    /** One "campaign" per active sprint (a role can have several: referral + direct). */
    const campaigns = useMemo(() => {
        return activeSprintJobs.map((job) => {
            const sprintId = job._sprintId;
            const key = String(sprintId);
            // Each card reflects its OWN sprint's day: the user's explicit pick,
            // otherwise the sprint's highest unlocked step.
            const sprintDay = dayBySprint[key] || highestUnlockedDayFor(job);
            const companyName = job.jobId?.companyName || 'Company';
            const role = job.jobId?.jobPosition || 'Open role';
            const logo = job.jobId?.companyId?.companyLogo || job.jobId?.companyLogo || '';
            const forSprint = (contactsByDay[sprintDay] || []).filter((m) => String(m._sprintId) === key);
            const total = getOutreachPlan(job)?.length || SPRINT_DAYS;
            // Derived from task data — days completed before this code shipped
            // count too, since the timeline is the source of truth.
            let completedSteps = 0;
            for (let d = 1; d <= total; d++) {
                if (isSprintDayComplete(key, d)) completedSteps += 1;
            }
            return {
                id: sprintId,
                sprintId,
                job,
                sourceId: job.jobId?._id || null,
                companyName,
                role,
                logo,
                pathId: job._pathId,
                pathTitle: job._pathTitle,
                strategyName: job._strategyName,
                kind: job._pathId === 'signal' ? 'signal' : 'job',
                saved: true,
                day: sprintDay,
                total,
                today: forSprint.length,
                done: forSprint.filter((m) => isMissionDoneForDay(m, sprintDay)).length,
                completedSteps,
            };
        });
    }, [activeSprintJobs, contactsByDay, dayBySprint, isMissionDoneForDay, isSprintDayComplete, highestUnlockedDayFor]);

    const filteredCampaigns = useMemo(() => {
        const q = campaignSearch.trim().toLowerCase();
        return campaigns.filter((c) => {
            if (campaignKindFilter === 'job' && c.kind !== 'job') return false;
            if (campaignKindFilter === 'signal' && c.kind !== 'signal') return false;
            if (campaignKindFilter === 'saved' && !c.saved) return false;
            if (campaignKindFilter === 'unsaved' && c.saved) return false;
            if (q && !`${c.companyName} ${c.role}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [campaigns, campaignKindFilter, campaignSearch]);

    const selectedCampaign = useMemo(() => {
        if (companyFilter !== 'all') {
            const match = campaigns.find((c) => String(c.id) === String(companyFilter));
            if (match) return match;
        }
        return filteredCampaigns[0] || campaigns[0] || null;
    }, [companyFilter, campaigns, filteredCampaigns]);

    // Group sprints → company → role → strategies, for the left-rail navigation.
    const campaignTree = useMemo(() => {
        const byCompany = new Map();
        for (const c of filteredCampaigns) {
            if (!byCompany.has(c.companyName)) {
                byCompany.set(c.companyName, { companyName: c.companyName, logo: c.logo, roles: new Map() });
            }
            const comp = byCompany.get(c.companyName);
            const roleKey = c.sourceId || c.role;
            if (!comp.roles.has(roleKey)) comp.roles.set(roleKey, { role: c.role, sprints: [] });
            comp.roles.get(roleKey).sprints.push(c);
        }
        return [...byCompany.values()].map((comp) => ({ ...comp, roles: [...comp.roles.values()] }));
    }, [filteredCampaigns]);

    // Effective day for the board: the selected campaign's day (explicit pick,
    // else its highest unlocked step — so opening any sprint lands on the
    // furthest actionable day, not Day 1).
    const currentDay = selectedCampaign ? selectedCampaign.day : 1;

    // Selected sprint's completed days — derived, drives the day strip + % stat.
    const completedDays = useMemo(() => {
        const days = new Set();
        if (!selectedCampaign) return days;
        for (let d = 1; d <= selectedCampaign.total; d++) {
            if (isSprintDayComplete(selectedCampaign.id, d)) days.add(d);
        }
        return days;
    }, [selectedCampaign, isSprintDayComplete]);

    // Marking a task pins the viewed day (via onCompleting below) so finishing
    // today's tasks never yanks the board to the next day — advancing is always
    // the user's click on "Go to Day N". Leaving a sprint unpins it, so the next
    // visit lands on its highest unlocked day again.
    const pinCurrentDay = useCallback(() => {
        if (selectedSprintKey !== 'all') setCurrentDay(currentDay);
    }, [selectedSprintKey, currentDay, setCurrentDay]);

    const prevSprintKeyRef = useRef(null);
    useEffect(() => {
        const prevKey = prevSprintKeyRef.current;
        if (prevKey === selectedSprintKey) return;
        prevSprintKeyRef.current = selectedSprintKey;
        if (!prevKey || prevKey === 'all') return;
        setDayBySprint((prev) => {
            if (prev[prevKey] == null) return prev;
            const { [prevKey]: _removed, ...rest } = prev;
            return rest;
        });
    }, [selectedSprintKey]);

    // FILTERED MISSION LIST
    const dailyMissions = useMemo(() => {
        const baseMissions = contactsByDay[currentDay] || [];
        // companyFilter holds the selected sprint id (or 'all').
        const filteredMissions = companyFilter === 'all'
            ? baseMissions
            : baseMissions.filter((mission) => String(mission._sprintId) === String(companyFilter));

        return filteredMissions.map((mission) => {
            const inviteStatus = isLinkedInConnected?.accountId
                ? (inviteStatusesByProviderId?.[mission?.providerId] || inviteStatusesByContactId?.[mission?.contactid || mission?._id])
                : undefined;

            const intent = inferMissionIntent(currentDay, mission?._taskName || mission?.name || '', mission?._dayChannel || '');

            if ((intent === 'connect' || intent === 'note') && inviteStatus === 'pending') {
                return {
                    ...mission,
                    _taskName: 'Connection request already pending — wait for response or nudge later'
                };
            }

            if ((intent === 'connect' || intent === 'note') && inviteStatus === 'accepted') {
                return {
                    ...mission,
                    _taskName: 'Already connected on LinkedIn — send a short follow-up message'
                };
            }

            return mission;
        });
    }, [contactsByDay, currentDay, companyFilter, inviteStatusesByProviderId, inviteStatusesByContactId]);

    const isDayCompleted = useCallback((day) => {
        if (companyFilter !== 'all') return isSprintDayComplete(companyFilter, day);
        const all = contactsByDay[day] || [];
        if (all.length === 0) return true; // nothing scheduled — trivially complete
        return all.every((c) => isMissionDoneForDay(c, day));
    }, [companyFilter, isSprintDayComplete, contactsByDay, isMissionDoneForDay]);

    // highestUnlockedDay + the day-clamp/auto-advance effects live further down,
    // after the selected sprint's plan is known (week steps are also time-gated).

    // --- UI HELPERS ---
    const getStrategyName = (id) => {
        if (STRATEGY_NAME_BY_ID[id]) return STRATEGY_NAME_BY_ID[id];
        return "Custom Strategy";
    };

    const getSprintProgress = () => {
        const completedCount = completedDays.size;
        return Math.min(Math.round((completedCount / (selectedSprintDays || SPRINT_DAYS)) * 100), 100);
    };

    const todayTotalCount = dailyMissions.length;
    const todayDoneCount = useMemo(
        () => dailyMissions.filter((mission) => isMissionDoneForDay(mission, currentDay)).length,
        [dailyMissions, currentDay, isMissionDoneForDay]
    );

    const activeCompaniesCount = useMemo(() => {
        const companiesSet = new Set(
            activeSprintJobs
                .map((job) => job.jobId?.companyName)
                .filter(Boolean)
        );
        return companiesSet.size;
    }, [activeSprintJobs]);

    // Distinct roles, not sprint rows — a role running referral + direct is one job.
    const activeRolesCount = useMemo(() => {
        const roleIds = new Set(
            activeSprintJobs
                .map((job) => String(job.jobId?._id || job._id || ''))
                .filter(Boolean)
        );
        return roleIds.size;
    }, [activeSprintJobs]);


    // (campaigns / filteredCampaigns / selectedCampaign / campaignTree are
    // defined earlier, before dailyMissions, because `currentDay` derives from
    // the selected campaign's own day.)

    // The selected sprint's plan length drives the header / day-rail / cadence.
    // Outreach-activated jobs use their own short plan (e.g. 4 days); everything
    // else falls back to the generic 14-day sprint.
    const selectedSprintPlan = getOutreachPlan(selectedCampaign?.job);
    const selectedSprintDays = selectedSprintPlan?.length || SPRINT_DAYS;
    const selectedPathTitle = getOutreachPathTitle(selectedCampaign?.job);
    const selectedStrategyName = selectedCampaign?.strategyName || null;

    // Signal sprints run a ~4-month, week-labelled nurture; job sprints are a
    // short day-by-day cadence. The plan steps already carry the right label
    // ("Week 0" / "Day 1"), so we render those labels and switch the surrounding
    // prose unit. Step progression stays index-based either way.
    const selectedSprintUnit = selectedCampaign?.kind === 'signal' ? 'week' : 'day';
    const unitCap = selectedSprintUnit === 'week' ? 'Week' : 'Day';
    const stepLabelFor = (step) => selectedSprintPlan?.[step - 1]?.label || `${unitCap} ${step}`;
    const selectedCadenceLabel = selectedSprintUnit === 'week'
        ? `${selectedSprintDays}-step · ~4-month nurture`
        : `${selectedSprintDays}-day plan`;

    // ── Time gating (week-cadence signal sprints) ──
    // A nurture's whole point is spacing: Week 3 should not be actionable the
    // moment Week 0 is done. Week steps unlock when their calendar date arrives
    // (activation date + the offset parsed from the step label, "Week 3" → +21
    // days). Day-cadence job sprints stay completion-gated only.
    const stepUnlockDate = useCallback((step) => {
        if (selectedSprintUnit !== 'week') return null;
        const activatedAtRaw = selectedCampaign?.job?._activatedAt;
        const activatedAt = activatedAtRaw ? new Date(activatedAtRaw) : null;
        if (!activatedAt || Number.isNaN(activatedAt.getTime())) return null;
        const m = /week\s*(\d+)/i.exec(String(selectedSprintPlan?.[step - 1]?.label || ''));
        if (!m) return null;
        return new Date(activatedAt.getTime() + Number(m[1]) * 7 * 24 * 60 * 60 * 1000);
    }, [selectedSprintUnit, selectedCampaign, selectedSprintPlan]);

    const isStepTimeLocked = useCallback((step) => {
        if ((earlyUnlocksBySprint[selectedSprintKey] || []).includes(step)) return false;
        const unlockAt = stepUnlockDate(step);
        return Boolean(unlockAt && Date.now() < unlockAt.getTime());
    }, [stepUnlockDate, earlyUnlocksBySprint, selectedSprintKey]);

    /** User chose to open the next step before its scheduled date. */
    const unlockNextStepEarly = () => {
        const next = currentDay + 1;
        if (next > selectedSprintDays) return;
        setEarlyUnlocksBySprint((prev) => {
            const arr = prev[selectedSprintKey] || [];
            return arr.includes(next) ? prev : { ...prev, [selectedSprintKey]: [...arr, next] };
        });
        setCurrentDay(next);
        toast.success(`${stepLabelFor(next)} unlocked early.`);
    };

    /** Selected sprint's unlock ceiling — same rule the rail's auto-day uses. */
    const highestUnlockedDay = useMemo(
        () => (selectedCampaign ? highestUnlockedDayFor(selectedCampaign.job) : 1),
        [selectedCampaign, highestUnlockedDayFor]
    );

    // Parked beyond what this sprint allows (sprint switch, shorter plan, or a
    // still-time-locked step) — snap back to the highest unlocked step.
    useEffect(() => {
        if (currentDay > highestUnlockedDay) setCurrentDay(highestUnlockedDay);
    }, [currentDay, highestUnlockedDay, setCurrentDay]);

    // (Completed days are derived from task data — see completedDays below —
    // so there is no recording step to keep in sync.)

    // When the current day flips to complete (user just hit "Mark done"), toast
    // and auto-advance. The ref tracks WHICH sprint+day it observed, so only a
    // fresh false→true transition on the same step advances — switching onto an
    // already-done day to review it must not kick the user forward.
    // Current step fully done and there IS a next step. Two flavors:
    //  - next step time-locked → "parked" banner with the unlock date + an
    //    unlock-early action;
    //  - next step already open (job sprints, or the date passed) → a simple
    //    "go to next" affordance for when the user is reviewing a finished day.
    const nextStepReady = companyFilter !== 'all'
        && currentDay < selectedSprintDays
        && todayTotalCount > 0
        && todayDoneCount === todayTotalCount;
    const parkedUntil = nextStepReady && isStepTimeLocked(currentDay + 1)
        ? stepUnlockDate(currentDay + 1)
        : null;

    const prevDayCompleteRef = useRef({ key: null, complete: false });
    useEffect(() => {
        if (!activeSprintJobs.length) return;
        const key = `${selectedSprintKey}::${currentDay}`;
        const complete = dailyMissions.length > 0 && isDayCompleted(currentDay);
        const prev = prevDayCompleteRef.current;
        prevDayCompleteRef.current = { key, complete };

        if (prev.key !== key || prev.complete || !complete) return;

        if (currentDay < selectedSprintDays) {
            const next = currentDay + 1;
            if (isStepTimeLocked(next)) {
                const unlockAt = stepUnlockDate(next);
                toast.success(`${stepLabelFor(currentDay)} complete — ${stepLabelFor(next)} unlocks${unlockAt ? ` on ${unlockAt.toLocaleDateString()}` : ' later'}.`);
            } else {
                // No auto-advance: the banner's "Go to Day N" button is the only
                // way forward, so the board never moves under the user.
                toast.success(`${stepLabelFor(currentDay)} complete — ${stepLabelFor(next)} is open.`);
            }
        } else {
            toast.success('🎉 Sprint complete — every day done!');
        }
    }, [isDayCompleted, currentDay, selectedSprintKey, selectedSprintDays, activeSprintJobs.length, dailyMissions, isStepTimeLocked, stepUnlockDate]); // eslint-disable-line react-hooks/exhaustive-deps

    // Default the board to the first sprint (so it shows one sprint, not all merged).
    useEffect(() => {
        if (activeSprintJobs.length === 0) return;
        if ((companyFilter === 'all' || !campaigns.some((c) => String(c.id) === String(companyFilter))) && selectedCampaign) {
            setCompanyFilter(selectedCampaign.id);
        }
    }, [activeSprintJobs.length, companyFilter, selectedCampaign, campaigns]);

    const cycleCampaign = (dir) => {
        const list = filteredCampaigns.length ? filteredCampaigns : campaigns;
        if (!list.length || !selectedCampaign) return;
        const idx = list.findIndex((c) => String(c.id) === String(selectedCampaign.id));
        const nextIdx = (idx + dir + list.length) % list.length;
        setCompanyFilter(list[nextIdx].id);
    };

    const CAMPAIGN_KIND_FILTERS = [
        { key: 'all', label: 'All' },
        { key: 'job', label: 'Jobs' },
        { key: 'signal', label: 'Signals' },
        { key: 'saved', label: 'Saved' },
        { key: 'unsaved', label: 'Unsaved' },
    ];
    const countForKindFilter = (key) => {
        if (key === 'all') return campaigns.length;
        if (key === 'job') return campaigns.filter((c) => c.kind === 'job').length;
        if (key === 'signal') return campaigns.filter((c) => c.kind === 'signal').length;
        if (key === 'saved') return campaigns.filter((c) => c.saved).length;
        return campaigns.filter((c) => !c.saved).length;
    };

    const handleRemoveJobFromDailyTasks = async (campaign) => {
        try {
            const sprintId = campaign?.id || campaign?.sprintId;
            if (!sprintId) return;
            await dispatch(removeActiveSprint(sprintId)).unwrap();
            // Drop the removed sprint's local buckets so re-activating it later starts clean.
            setDayBySprint((prev) => {
                const { [String(sprintId)]: _removed, ...rest } = prev;
                return rest;
            });
            setEarlyUnlocksBySprint((prev) => {
                const { [String(sprintId)]: _removed, ...rest } = prev;
                return rest;
            });
            if (String(companyFilter) === String(sprintId)) {
                setCompanyFilter('all');
            }
        } catch (err) {
            console.error('Failed to remove sprint from daily tasks:', err);
        }
    };

    return (
        <div className="relative h-screen bg-[#FCFCFC] text-slate-900 selection:bg-indigo-500/10 font-dmsans pt-16 flex flex-col overflow-hidden">
            <Navbaar />
            
            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* Original Main Sidebar */}
                <aside className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </aside>

                {/* Main Dashboard Area */}
                <main className="flex-1 flex flex-col overflow-y-auto custom-scrollbar bg-gradient-to-b from-indigo-50/60 via-[#FCFCFC] to-[#FCFCFC] text-left">
                    
                    {/* Hero */}
                    <div className="px-6 md:px-8 pt-7 pb-4 text-left">
                        <h1 className="font-bold tracking-tight text-slate-900 leading-tight text-2xl">
                            Daily Task Board
                        </h1>
                        <p className="text-sm text-slate-500 mt-1.5 max-w-xl">
                            Focused, actionable tasks generated from your saved jobs and outreach strategy.
                        </p>
                    </div>

                    {activeSprints.length === 0 && (activeSprintsLoading || !activeSprintsFetched) ? (
                        /* ── Loading skeleton (until first fetch resolves) ── */
                        <DailyTasksSkeleton />
                    ) : activeSprintJobs.length === 0 ? (
                        /* ── Empty state ── */
                        <div className="px-6 md:px-8 pb-8 pt-2 flex-1 flex">
                            <div className="w-full flex">
                                <div className="relative rounded-3xl flex-1 flex flex-col">
                                    <div className="relative px-8 py-10 flex-1 flex flex-col items-center justify-center text-center">
                                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3F57E0] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-indigo-500/20">
                                            <Sparkles size={28} className="text-white" />
                                        </div>
                                        <h3 className="text-[22px] font-bold tracking-tight text-slate-900 mt-5">No active sprints yet</h3>
                                        <p className="text-[13.5px] text-slate-500 leading-relaxed mt-2 max-w-md">
                                            Activate an outreach strategy from any <span className="font-semibold text-slate-700">job</span> or{' '}
                                            <span className="font-semibold text-slate-700">hiring signal</span> — saved or unsaved — and your day-by-day plan lands here automatically.
                                        </p>

                                        <div className="flex flex-col sm:flex-row items-center gap-2.5 mt-6 w-full sm:w-auto">
                                            <button
                                                onClick={() => navigate('/')}
                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#3F57E0] text-white rounded-xl text-[13.5px] font-semibold hover:bg-[#3349c9] active:scale-[0.98] transition-all"
                                            >
                                                <Briefcase size={16} /> Browse jobs
                                            </button>
                                            <button
                                                // Repointed, not removed: this is a generic
                                                // "go look at signals" CTA and the public
                                                // board serves it fully. Was '/hiringsignal'.
                                                onClick={() => navigate('/hiring-signals-us')}
                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-[#E6E8EE] text-slate-700 rounded-xl text-[13.5px] font-semibold hover:border-[#3F57E0] hover:text-[#3F57E0] active:scale-[0.98] transition-all"
                                            >
                                                <Radio size={16} /> Explore hiring signals
                                            </button>
                                        </div>
                                    </div>

                                    {/* How it works */}
                                    <div className="relative border-t border-[#EEF0F4] grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#EEF0F4]">
                                        {[
                                            { icon: Search, t: 'Open a job or signal', d: 'Saved, or fresh from search.' },
                                            { icon: Target, t: 'Choose a strategy', d: 'Referral, direct, or signal play.' },
                                            { icon: Sparkles, t: 'Activate', d: 'Your daily plan appears here.' },
                                        ].map((step, i) => {
                                            const Icon = step.icon;
                                            return (
                                                <div key={i} className="flex items-start gap-3 px-5 py-4">
                                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                                                        <Icon size={15} className="text-[#3F57E0]" />
                                                    </div>
                                                    <div>
                                                        <p className="text-[12.5px] font-bold text-slate-800 leading-tight">{step.t}</p>
                                                        <p className="text-[11.5px] text-slate-400 mt-0.5 leading-snug">{step.d}</p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* ── v3 sprint workspace (campaign rail · board · cadence) ── */
                        <div className="px-6 md:px-8 pb-10 grid grid-cols-1 xl:grid-cols-[272px_1fr_300px] gap-4 items-start">

                            {/* LEFT — campaign switcher rail */}
                            <aside className="hidden xl:flex flex-col bg-white border border-[#ECECEC] rounded-2xl p-3 xl:sticky xl:top-4 self-start" style={{ maxHeight: 'calc(100vh - 140px)' }}>
                                <div className="px-1 pb-2.5">
                                    <h3 className="text-[13.5px] font-semibold text-slate-900">Your sprints</h3>
                                    <p className="text-[11px] text-slate-400 mt-0.5">{campaigns.length} active {campaigns.length === 1 ? 'strategy' : 'strategies'}</p>
                                </div>

                                <div className="flex items-center gap-2 bg-[#F4F5F8] rounded-lg px-2.5 py-2 mb-2.5">
                                    <Search size={13} className="text-slate-400 shrink-0" />
                                    <input
                                        value={campaignSearch}
                                        onChange={(e) => setCampaignSearch(e.target.value)}
                                        placeholder="Find a company…"
                                        className="flex-1 min-w-0 bg-transparent outline-none text-[12.5px] text-slate-700 placeholder:text-slate-400"
                                    />
                                </div>

                                <div className="flex flex-wrap gap-1.5 mb-3">
                                    {CAMPAIGN_KIND_FILTERS.map((f) => {
                                        const on = campaignKindFilter === f.key;
                                        return (
                                            <button
                                                key={f.key}
                                                onClick={() => setCampaignKindFilter(f.key)}
                                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${on ? 'bg-[#3F57E0] text-white border-[#3F57E0]' : 'bg-white text-slate-600 border-[#E6E8EE] hover:border-slate-300'}`}
                                            >
                                                {f.key === 'job' && <Briefcase size={10} />}
                                                {f.key === 'signal' && <Radio size={10} />}
                                                {f.key === 'saved' && <Bookmark size={10} />}
                                                {f.label}
                                                {f.key === 'all' && <span className="opacity-70">{countForKindFilter(f.key)}</span>}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="flex flex-col gap-3 overflow-y-auto custom-scrollbar -mr-1 pr-1">
                                    {campaignTree.length > 0 ? campaignTree.map((comp) => (
                                        <div key={comp.companyName} className="rounded-xl border border-[#EEF0F4] bg-white/60">
                                            {/* Company */}
                                            <div className="flex items-center gap-2.5 px-3 pt-3 pb-2">
                                                <LogoTile name={comp.companyName} logo={comp.logo} size={32} />
                                                <div className="min-w-0">
                                                    <div className="text-[13px] font-bold text-slate-900 truncate">{comp.companyName}</div>
                                                    <div className="text-[10.5px] text-slate-400">{comp.roles.length} role{comp.roles.length !== 1 ? 's' : ''}</div>
                                                </div>
                                            </div>
                                            {/* Roles → strategies */}
                                            <div className="px-2 pb-2 flex flex-col gap-2">
                                                {comp.roles.map((roleGroup, ri) => (
                                                    <div key={ri} className="rounded-lg bg-slate-50/70 p-1.5">
                                                        <div className="px-1.5 py-1 text-[11px] font-semibold text-slate-500 truncate">{roleGroup.role}</div>
                                                        <div className="flex flex-col gap-1">
                                                            {roleGroup.sprints.map((c) => {
                                                                const selected = String(selectedCampaign?.id) === String(c.id);
                                                                // Count = today's tasks; bar = overall sprint progress.
                                                                // (A today-only bar reset to 0% each day and looked stuck.)
                                                                const todayPct = c.today > 0 ? Math.round((c.done / c.today) * 100) : 0;
                                                                const sprintPct = c.total > 0 ? Math.round((c.completedSteps / c.total) * 100) : 0;
                                                                return (
                                                                    <div
                                                                        key={c.id}
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        onClick={() => setCompanyFilter(c.id)}
                                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCompanyFilter(c.id); } }}
                                                                        className={`group relative text-left rounded-lg px-2.5 py-2 cursor-pointer transition-all border ${selected ? 'bg-white border-[#3F57E0] shadow-[0_2px_10px_rgba(63,87,224,0.12)]' : 'bg-white/70 border-transparent hover:border-slate-200'}`}
                                                                    >
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); setPendingRemove(c); }}
                                                                            title="Remove this strategy"
                                                                            className="absolute top-1.5 right-1.5 w-5 h-5 inline-flex items-center justify-center rounded text-slate-400 opacity-40 group-hover:opacity-100 focus-visible:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all"
                                                                        >
                                                                            <Trash2 size={12} />
                                                                        </button>
                                                                        <div className="flex items-center gap-1.5 pr-5">
                                                                            <span className={`text-[9px] font-bold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded ${c.pathId === 'referral' ? 'bg-violet-50 text-violet-700' : c.pathId === 'signal' ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-[#3F57E0]'}`}>
                                                                                {c.pathTitle || (c.pathId === 'referral' ? 'Referral' : 'Direct')}
                                                                            </span>
                                                                        </div>
                                                                        {c.strategyName && (
                                                                            <div className="text-[12px] font-semibold text-slate-800 truncate mt-1 pr-5">{c.strategyName}</div>
                                                                        )}
                                                                        <div className="flex items-center gap-2 mt-1.5">
                                                                            <span className="text-[10.5px] font-semibold text-slate-400">{c.kind === 'signal' ? `${getOutreachPlan(c.job)?.[c.day - 1]?.label || `Week ${c.day}`} · ${c.day}/${c.total}` : `Day ${c.day}/${c.total}`}</span>
                                                                            <span className="flex-1" />
                                                                            <span title="Today's tasks done" className={`text-[10.5px] font-bold ${c.today > 0 && todayPct === 100 ? 'text-emerald-600' : 'text-slate-500'}`}>{c.done}/{c.today}</span>
                                                                        </div>
                                                                        <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden mt-1.5" title={`Sprint progress: ${sprintPct}%`}>
                                                                            <div className={`h-full rounded-full ${sprintPct === 100 ? 'bg-emerald-500' : 'bg-[#3F57E0]'}`} style={{ width: `${sprintPct}%` }} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="text-center text-[12px] text-slate-400 py-8">No sprints match.</div>
                                    )}
                                </div>
                            </aside>

                            {/* CENTER — selected campaign board */}
                            <div className="flex flex-col gap-4 min-w-0">

                                {/* Active campaign header */}
                                {selectedCampaign && (
                                    <div className="rounded-2xl border border-indigo-100 p-5" style={{ background: 'linear-gradient(135deg, #F4F6FF 0%, #FFFFFF 70%)' }}>
                                        <div className="flex items-center gap-3.5 flex-wrap">
                                            <LogoTile name={selectedCampaign.companyName} logo={selectedCampaign.logo} size={44} />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[9.5px] font-bold uppercase tracking-[0.05em] bg-[#3F57E0] text-white px-2 py-0.5 rounded-full">Active sprint</span>
                                                    {selectedPathTitle && (
                                                        <span className="text-[9.5px] font-bold uppercase tracking-[0.05em] text-[#3F57E0] bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-full">{selectedPathTitle}</span>
                                                    )}
                                                    <span className="text-[11px] font-semibold text-slate-500">{stepLabelFor(currentDay)} · {currentDay} of {selectedSprintDays}</span>
                                                </div>
                                                <h2 className="text-[18px] font-bold text-slate-900 mt-1.5 truncate">{selectedCampaign.companyName} <span className="text-slate-400 font-medium text-[13px]">· {selectedCampaign.role}</span></h2>
                                                {selectedStrategyName && (
                                                    <p className="text-[12.5px] font-semibold text-[#3F57E0] mt-0.5 truncate">{selectedStrategyName}</p>
                                                )}
                                            </div>
                                            {campaigns.length > 1 && (
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <button onClick={() => cycleCampaign(-1)} title="Previous sprint" className="w-[34px] h-[34px] inline-flex items-center justify-center rounded-lg border border-[#E6E8EE] bg-white text-slate-600 hover:bg-slate-50 transition-colors"><ChevronLeft size={15} /></button>
                                                    <button onClick={() => cycleCampaign(1)} title="Next sprint" className="w-[34px] h-[34px] inline-flex items-center justify-center rounded-lg border border-[#E6E8EE] bg-white text-slate-600 hover:bg-slate-50 transition-colors"><ChevronRight size={15} /></button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Stats */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    <div className="bg-white rounded-xl px-4 py-3.5 border border-[#ECECEC] text-left">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">Active jobs</p>
                                        <p className="text-2xl font-extrabold tracking-tight text-slate-900 leading-none mt-1.5">{activeRolesCount}</p>
                                        <p className="text-[11px] text-slate-400 mt-1.5">across {activeSprintJobs.length} sprint{activeSprintJobs.length !== 1 ? 's' : ''}</p>
                                    </div>
                                    <div className="bg-white rounded-xl px-4 py-3.5 border border-[#ECECEC] text-left">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">Companies</p>
                                        <p className="text-2xl font-extrabold tracking-tight text-slate-900 leading-none mt-1.5">{activeCompaniesCount}</p>
                                        <p className="text-[11px] text-slate-400 mt-1.5">tracked</p>
                                    </div>
                                    <div className="bg-white rounded-xl px-4 py-3.5 border border-[#ECECEC] text-left">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">Today</p>
                                        <p className="text-2xl font-extrabold tracking-tight text-[#3F57E0] leading-none mt-1.5">
                                            {todayDoneCount}
                                            <span className="text-slate-300 font-bold text-base">/{todayTotalCount}</span>
                                        </p>
                                        <p className="text-[11px] text-slate-400 mt-1.5">tasks done</p>
                                    </div>
                                    <div className="bg-white rounded-xl px-4 py-3.5 border border-[#ECECEC] text-left">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">Sprint</p>
                                        <p className="text-2xl font-extrabold tracking-tight text-[#3F57E0] leading-none mt-1.5">{getSprintProgress()}%</p>
                                        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden mt-2.5">
                                            <div className="h-full bg-[#3F57E0] transition-all duration-700 ease-out rounded-full" style={{ width: `${getSprintProgress()}%` }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Sprint day plan */}
                                <div className="bg-white border border-[#ECECEC] rounded-2xl p-4">
                                    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                                        <div>
                                            <p className="text-xs font-semibold text-slate-500 tracking-wide">{selectedCadenceLabel}</p>
                                            <p className="text-[11px] text-slate-400 mt-0.5">
                                                {selectedSprintUnit === 'week'
                                                    ? 'Finish each week’s tasks — the next week unlocks on its scheduled date.'
                                                    : 'Finish every task in a day to unlock the next.'}
                                            </p>
                                        </div>
                                        <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#3F57E0] bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-full">
                                            Current · {stepLabelFor(currentDay)}
                                        </span>
                                    </div>
                                    <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1">
                                        {Array.from({ length: selectedSprintDays }).map((_, i) => {
                                            const day = i + 1;
                                            const isActive = currentDay === day;
                                            const isCompleted = completedDays.has(day);
                                            const isLocked = day > highestUnlockedDay;
                                            const unlockAt = isLocked && isStepTimeLocked(day) ? stepUnlockDate(day) : null;
                                            const lockTitle = unlockAt
                                                ? `Unlocks on ${unlockAt.toLocaleDateString()} — nurture steps are spaced on purpose`
                                                : `Complete ${stepLabelFor(day - 1)} first`;
                                            return (
                                                <div
                                                    key={day}
                                                    role="button"
                                                    tabIndex={isLocked ? -1 : 0}
                                                    onKeyDown={(e) => {
                                                        if (isLocked) return;
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setCurrentDay(day);
                                                        }
                                                    }}
                                                    title={isLocked ? lockTitle : `View ${stepLabelFor(day)}`}
                                                    onClick={() => !isLocked && setCurrentDay(day)}
                                                    className={`relative shrink-0 h-10 min-w-[76px] px-3 rounded-lg border transition-all duration-200 cursor-pointer flex items-center justify-center gap-1.5 ${isActive
                                                        ? 'bg-[#3F57E0] border-[#3F57E0] text-white z-10'
                                                        : isLocked
                                                            ? 'bg-slate-50 border-slate-100 text-slate-300 opacity-70 cursor-not-allowed'
                                                            : isCompleted
                                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                                                                : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-200 hover:bg-slate-50'}`}
                                                >
                                                    <span className="text-[11px] font-medium whitespace-nowrap">{stepLabelFor(day)}</span>
                                                    {unlockAt && (
                                                        <span className="text-[9px] font-semibold whitespace-nowrap opacity-80">
                                                            {unlockAt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                                        </span>
                                                    )}
                                                    {isCompleted ? (
                                                        <CheckCircle2 size={13} className={isActive ? "text-white" : "text-emerald-600"} />
                                                    ) : isLocked ? (
                                                        <ShieldCheck size={13} className={isActive ? "text-white" : "text-slate-300"} />
                                                    ) : (
                                                        <Circle size={13} className={isActive ? "text-white/80" : "text-slate-300"} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Day tasks */}
                                <div className="flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-900 tracking-tight">
                                                {stepLabelFor(currentDay)} — Tasks
                                            </h2>
                                            <p className="text-xs text-slate-400 mt-0.5">
                                                {todayDoneCount} of {todayTotalCount} completed
                                                {todayTotalCount > 0 && todayDoneCount === todayTotalCount && (
                                                    <span className="ml-2 text-emerald-600 font-medium">All done</span>
                                                )}
                                            </p>
                                        </div>
                                        {todayTotalCount > 0 && (
                                            <div className="flex items-center gap-2">
                                                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-emerald-500 transition-all duration-500 rounded-full"
                                                        style={{ width: `${todayTotalCount > 0 ? (todayDoneCount / todayTotalCount) * 100 : 0}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs text-slate-500 font-medium">
                                                    {todayTotalCount > 0 ? Math.round((todayDoneCount / todayTotalCount) * 100) : 0}%
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {parkedUntil ? (
                                        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200">
                                            <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[12.5px] text-emerald-800 leading-relaxed">
                                                    <b>{stepLabelFor(currentDay)} complete.</b>{' '}
                                                    {stepLabelFor(currentDay + 1)} unlocks on{' '}
                                                    <b>{parkedUntil.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</b>{' '}
                                                    — spacing the touches is what makes a nurture land. You can open it now if you'd rather not wait.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={unlockNextStepEarly}
                                                    className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-emerald-800 bg-white border border-emerald-300 hover:bg-emerald-100 active:scale-[0.98] transition-all"
                                                >
                                                    Unlock {stepLabelFor(currentDay + 1)} now
                                                </button>
                                            </div>
                                        </div>
                                    ) : nextStepReady ? (
                                        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 flex-wrap">
                                            <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
                                            <p className="text-[12.5px] text-emerald-800 leading-relaxed flex-1 min-w-0">
                                                <b>{stepLabelFor(currentDay)} complete.</b> {stepLabelFor(currentDay + 1)} is open.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setCurrentDay(currentDay + 1)}
                                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] transition-all"
                                            >
                                                Go to {stepLabelFor(currentDay + 1)} <ArrowRight size={13} />
                                            </button>
                                        </div>
                                    ) : null}

                                    {dailyMissions.length > 0 ? (
                                        <div className="flex flex-col gap-3">
                                            {dailyMissions.map((contact, index) => (
                                                <DTCard
                                                    key={getDailyTaskPersonKey(contact) || `${contact?._companyName || 'company'}-${contact?.name || index}`}
                                                    contact={contact}
                                                    currentDay={currentDay}
                                                    contactPools={contactsByCompanyId?.[contact?._organizationId] ?? EMPTY_CONTACTS}
                                                    taskHistory={taskHistoryByContact[contact?.contactid || contact?._id] ?? EMPTY_OBJ}
                                                    onCompleting={pinCurrentDay}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-12 rounded-2xl border border-dashed border-slate-300 flex flex-col items-start justify-center text-left bg-white">
                                            <div className="w-16 h-16 bg-slate-50 rounded-xl flex items-center justify-center mb-6">
                                                <LayoutDashboard size={40} className="text-slate-300" />
                                            </div>
                                            <h3 className="text-xl font-semibold text-slate-900 mb-2">No tasks for this {selectedSprintUnit} yet</h3>
                                            <p className="text-slate-500 text-sm max-w-sm leading-relaxed">
                                                This sprint has no tasks scheduled for {stepLabelFor(currentDay)}. Pick another {selectedSprintUnit} above or switch sprints.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT — how tasks help + cadence */}
                            <div className="flex flex-col gap-4 xl:sticky xl:top-4 self-start">
                                <div className="bg-white rounded-2xl p-5 border border-[#ECECEC]">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="w-7 h-7 rounded-lg bg-indigo-50 text-[#3F57E0] flex items-center justify-center">
                                            <Sparkles size={14} />
                                        </div>
                                        <h3 className="text-slate-900 font-semibold text-[13.5px]">How today's tasks help</h3>
                                    </div>
                                    <p className="text-slate-500 text-[12.5px] leading-relaxed">
                                        Tasks are prioritized using job recency, fit score, and strategy type — so you spend time on the highest-impact actions first.
                                    </p>
                                    <div className="grid grid-cols-2 gap-2.5 mt-3.5">
                                        <div className="rounded-[10px] bg-[#FAFBFD] border border-[#EFF1F5] px-3.5 py-3">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">Open tasks</p>
                                            <p className="text-[22px] font-extrabold text-slate-900 mt-1 leading-none">{Math.max(todayTotalCount - todayDoneCount, 0)}</p>
                                        </div>
                                        <div className="rounded-[10px] bg-emerald-50 border border-emerald-200 px-3.5 py-3">
                                            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-emerald-700">Completed</p>
                                            <p className="text-[22px] font-extrabold text-emerald-700 mt-1 leading-none">{todayDoneCount}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-5 border border-[#ECECEC]">
                                    <h4 className="text-slate-900 text-[13.5px] font-semibold mb-3 flex items-center gap-2">
                                        <Clock size={14} className="text-[#3F57E0]" />
                                        {selectedSprintUnit === 'week' ? 'Nurture cadence' : `${selectedSprintDays}-day cadence`}
                                    </h4>
                                    {(selectedSprintPlan
                                        ? selectedSprintPlan.map((s, i) => ({ d: s.label || `${unitCap} ${i + 1}`, t: s.goal || s.action || '', upTo: i + 1 }))
                                        : [
                                            { d: 'Days 1–3', t: 'Build visibility and warm contact touchpoints.', upTo: 3 },
                                            { d: 'Day 4', t: 'Send first-value outreach emails.', upTo: 4 },
                                            { d: 'Days 5–7', t: 'Continue engagement and follow-ups.', upTo: 7 },
                                            { d: 'Days 10–14', t: 'Convert momentum into referrals.', upTo: 14 }
                                        ]
                                    ).map((c, i, arr) => {
                                        const phaseDone = currentDay > c.upTo;
                                        const prevUpTo = i === 0 ? 0 : arr[i - 1].upTo;
                                        const phaseCurrent = !phaseDone && currentDay > prevUpTo;
                                        return (
                                            <div key={i} className={`flex gap-2.5 py-2.5 ${i < arr.length - 1 ? 'border-b border-[#EFF1F5]' : ''}`}>
                                                <div className={`w-[18px] h-[18px] rounded-full shrink-0 mt-0.5 flex items-center justify-center ${phaseDone ? 'bg-emerald-500' : phaseCurrent ? 'bg-[#3F57E0]' : 'bg-slate-100'}`}>
                                                    {phaseDone ? (
                                                        <Check size={10} className="text-white" strokeWidth={3} />
                                                    ) : (
                                                        <span className={`w-1.5 h-1.5 rounded-full ${phaseCurrent ? 'bg-white' : 'bg-slate-300'}`} />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className={`text-[12.5px] font-bold ${phaseCurrent ? 'text-[#3F57E0]' : 'text-slate-900'}`}>{c.d}</p>
                                                    <p className="text-[11px] text-slate-400 leading-snug mt-0.5">{c.t}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </main>

                {/* Remove-sprint confirmation */}
                {pendingRemove && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
                        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setPendingRemove(null)} />
                        <div className="relative w-full max-w-[400px] rounded-2xl bg-white shadow-2xl p-6 text-center">
                            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                                <Trash2 size={24} className="text-red-600" />
                            </div>
                            <h3 className="text-[17px] font-bold text-slate-900 mt-4">Remove this sprint?</h3>
                            <p className="text-[13px] text-slate-500 mt-2 leading-relaxed">
                                This removes <span className="font-semibold text-slate-700">{pendingRemove.companyName}</span> from your Daily Tasks and clears its active strategy. Your saved job and contacts are kept.
                            </p>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setPendingRemove(null)}
                                    className="flex-1 py-2.5 rounded-xl text-[13.5px] font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors active:scale-[0.98]"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => { const campaign = pendingRemove; setPendingRemove(null); handleRemoveJobFromDailyTasks(campaign); }}
                                    className="flex-1 py-2.5 rounded-xl text-[13.5px] font-bold text-white bg-red-600 hover:bg-red-700 transition-colors active:scale-[0.98]"
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { 
                    background: #e2e8f0; 
                    border-radius: 10px; 
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
            `}</style>
        </div>
    );
};

export default DailyTasks;
